import {
	findScrollParent,
	findSpatialTarget,
	isVisibleInScrollParents
} from './spatialNavGeometry'

const FOCUSABLE = '.control:not(:disabled)'

export type Dir = 'up' | 'down' | 'left' | 'right'
type TextInput = HTMLInputElement | HTMLTextAreaElement

const isTextInputEl = (el: HTMLElement): el is TextInput =>
	el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement

const isSelectEl = (el: HTMLElement): el is HTMLSelectElement =>
	el instanceof HTMLSelectElement

const getControlRoot = (el: HTMLElement): HTMLElement =>
	el.closest<HTMLElement>('.control') ?? el

const getControlFocusTarget = (el: HTMLElement): HTMLElement =>
	el.matches('input, textarea, select, button, a, [tabindex]')
		? el
		: (el.querySelector<HTMLElement>(
				'input:not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
			) ?? el)

const focusControl = (el: HTMLElement) => {
	const target = getControlFocusTarget(el)
	target.focus()
	target.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}

const keyToDir: Record<string, Dir> = {
	ArrowUp: 'up',
	ArrowDown: 'down',
	ArrowLeft: 'left',
	ArrowRight: 'right'
}

let spatialNavDocument: Document | null = null
let cleanupSpatialNav: (() => void) | null = null

function atTextBoundary(el: HTMLElement, dir: Dir): boolean {
	if (el.tagName === 'SELECT') return true

	if (el.isContentEditable) {
		const sel = window.getSelection()
		if (!sel || sel.rangeCount === 0) return true
		if (!sel.isCollapsed) return false
		const range = sel.getRangeAt(0)
		if (dir === 'left' || dir === 'up') {
			return (
				(range.startOffset === 0 &&
					range.startContainer === el.firstChild) ||
				range.startContainer === el
			)
		}
		const container = range.endContainer
		const atEnd =
			container.nodeType === Node.TEXT_NODE
				? range.endOffset === (container.textContent?.length ?? 0)
				: range.endOffset === container.childNodes.length
		if (!atEnd) return false
		let node: Node | null = container
		while (node && node !== el) {
			if (node.nextSibling) return false
			node = node.parentNode
		}
		return true
	}

	if (!isTextInputEl(el)) return true
	if (el.selectionStart == null) return true
	if (el.selectionStart !== el.selectionEnd) return false

	const pos = el.selectionStart
	const len = el.value.length

	if (dir === 'left' || dir === 'up') return pos === 0
	return pos === len
}

export function initSpatialNav(doc?: Document) {
	const resolvedDocument =
		doc ?? (typeof document !== 'undefined' ? document : undefined)
	if (resolvedDocument == null) return () => {}
	if (spatialNavDocument === resolvedDocument && cleanupSpatialNav) {
		return cleanupSpatialNav
	}

	cleanupSpatialNav?.()

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter' && e.target instanceof HTMLElement) {
			const target = e.target
			if (isSelectEl(target)) {
				e.preventDefault()
				target.showPicker?.()
			}
			return
		}

		const dir = keyToDir[e.key]
		if (!dir) return

		if (!(e.target instanceof HTMLElement)) return
		const target = e.target

		if (target.tagName === 'SELECT') e.preventDefault()

		const currentControl = getControlRoot(target)
		const isTextInput =
			target.tagName === 'INPUT' ||
			target.tagName === 'TEXTAREA' ||
			target.tagName === 'SELECT' ||
			target.isContentEditable
		if (isTextInput && !atTextBoundary(target, dir)) return

		// scope to popover/dialog container if inside one
		const scopeEl = target.closest('[popover], [role="dialog"]')
		const scope = scopeEl instanceof HTMLElement ? scopeEl : null
		const root = scope ?? resolvedDocument

		const all = Array.from(
			root.querySelectorAll<HTMLElement>(FOCUSABLE)
		).filter(
			(el) =>
				el !== currentControl &&
				!el.closest('[inert]') &&
				el.checkVisibility({ checkOpacity: true })
		)
		if (all.length === 0) return

		const source = target === document.body ? null : currentControl
		const fromRect = source
			? currentControl.getBoundingClientRect()
			: new DOMRect(window.innerWidth / 2, window.innerHeight / 2, 0, 0)

		const scrollParent = source ? findScrollParent(currentControl) : null
		if (scrollParent) {
			if (!isVisibleInScrollParents(currentControl)) {
				// focus is scrolled out of view — re-anchor to nearest visible element
				const visible = all.filter(
					(el) =>
						scrollParent.contains(el) &&
						isVisibleInScrollParents(el)
				)
				if (visible.length > 0) {
					const nearest = visible.reduce((best, el) => {
						const r = el.getBoundingClientRect()
						const br = best.getBoundingClientRect()
						const d =
							Math.abs(r.top - fromRect.top) +
							Math.abs(r.left - fromRect.left)
						const bd =
							Math.abs(br.top - fromRect.top) +
							Math.abs(br.left - fromRect.left)
						return d < bd ? el : best
					})
					e.preventDefault()
					focusControl(nearest)
				}
				return
			}
			// try within scroll container first (including clipped elements)
			const inside = all.filter((el) => scrollParent.contains(el))
			const nextInside = findSpatialTarget(source, fromRect, inside, dir)
			if (nextInside) {
				e.preventDefault()
				focusControl(nextInside)
				return
			}
			// nothing inside container in this direction — escape
			const outside = all.filter(
				(el) =>
					!scrollParent.contains(el) && isVisibleInScrollParents(el)
			)
			const nextOutside = findSpatialTarget(
				source,
				fromRect,
				outside,
				dir
			)
			if (nextOutside) {
				e.preventDefault()
				focusControl(nextOutside)
				return
			}
		} else {
			const visible = all.filter((el) => isVisibleInScrollParents(el))
			const next = findSpatialTarget(source, fromRect, visible, dir)
			if (!next) return
			e.preventDefault()
			focusControl(next)
		}
	}

	spatialNavDocument = resolvedDocument
	resolvedDocument.addEventListener('keydown', onKeyDown)

	cleanupSpatialNav = () => {
		resolvedDocument.removeEventListener('keydown', onKeyDown)
		if (spatialNavDocument === resolvedDocument) {
			spatialNavDocument = null
			cleanupSpatialNav = null
		}
	}

	return cleanupSpatialNav
}
